import {
  DRIFT_THRESHOLD,
  FOLLOWER_TICK_MS,
  LEADER_BROADCAST_MS,
  POST_CHANGE_CHECK_MS,
  POST_CHANGE_TOLERANCE,
  computeExpectedTime,
} from '@anisync/types';
import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';
import type { SyncState } from './use-room';

/**
 * Synchronizacja odtwarzania — cztery niezależne efekty.
 *
 * Podział na cztery nie jest przypadkiem; każdy pokrywa inny tryb rozjechania się:
 *
 *  1. **Takt prowadzącego** — rozsyła jego pozycję, żeby pozostali mieli do czego
 *     dążyć.
 *  2. **Takt followera** — koryguje dryf, ale tylko powyżej progu. Seek jest
 *     widoczny i drażniący, więc poprawianie o pół sekundy pogarszałoby wrażenie
 *     zamiast je naprawiać.
 *  3. **Natychmiastowe zastosowanie** — pauza i przewinięcie muszą działać od
 *     razu, bez czekania na najbliższy takt.
 *  4. **Kontrola po zmianie materiału** — łapie klienta, który utknął na
 *     buforowaniu i nie dogonił mimo taktów.
 *
 * Wszystkie liczą czas przez `computeExpectedTime` — jedyne miejsce z tą
 * matematyką, pokryte testami. Powielenie wzoru tutaj dałoby dwie wersje prawdy,
 * rozjeżdżające się przy pierwszej zmianie stałej.
 */

export interface PlaybackSyncOptions {
  videoRef: RefObject<HTMLVideoElement | null>;
  sync: SyncState;
  offsetMs: number;
  isLeader: boolean;
  /** Gotowość materiału — przed nią element `<video>` nie istnieje. */
  ready: boolean;
  /** Rośnie przy każdej zmianie materiału. */
  mediaEpoch: number;
  onBroadcast: (time: number, paused: boolean) => void;
  onEnded: () => void;
}

export function usePlaybackSync(options: PlaybackSyncOptions): { correctedAt: number | null } {
  const { videoRef, sync, offsetMs, isLeader, ready, mediaEpoch, onBroadcast, onEnded } = options;
  const [correctedAt, setCorrectedAt] = useState<number | null>(null);

  /**
   * Najświeższy stan synchronizacji w refie.
   *
   * Stopery czytają go w chwili wystrzelenia, a nie w chwili zakładania. Gdyby
   * `sync` był zależnością efektu, interwał followera byłby niszczony
   * i zakładany od nowa przy KAŻDYM takcie prowadzącego, czyli co trzy sekundy —
   * a przy odtwarzaniu z jednosekundowym taktem gubiłoby to część sprawdzeń.
   */
  const syncRef = useRef(sync);
  const offsetRef = useRef(offsetMs);
  useEffect(() => {
    syncRef.current = sync;
    offsetRef.current = offsetMs;
  }, [sync, offsetMs]);

  /**
   * Czyta WYŁĄCZNIE refy, więc jest stabilny i może być jawną zależnością
   * efektów — bez tego linter słusznie zgłaszałby brakujące zależności,
   * a dopisanie tam `sync` przywróciłoby przebudowywanie interwału co takt.
   */
  const expectedNow = useCallback(
    (): number =>
      computeExpectedTime({
        syncTime: syncRef.current.time,
        syncEmittedAt: syncRef.current.emittedAt,
        isPaused: syncRef.current.paused,
        clockOffset: offsetRef.current,
      }),
    [],
  );

  /* 1. Takt prowadzącego. */
  useEffect(() => {
    if (!ready || !isLeader) return;
    const video = videoRef.current;
    if (video === null) return;

    const tick = (): void => onBroadcast(video.currentTime, video.paused);
    // Natychmiast po przejęciu prowadzenia, żeby pozostali nie czekali trzech
    // sekund na pierwszą informację o pozycji.
    tick();
    const timer = setInterval(tick, LEADER_BROADCAST_MS);

    // Pauza i przewinięcie idą od razu, poza taktem — inaczej pozostali
    // odtwarzaliby dalej do trzech sekund po zatrzymaniu u prowadzącego.
    const onPause = (): void => onBroadcast(video.currentTime, true);
    const onPlay = (): void => onBroadcast(video.currentTime, false);
    const onSeeked = (): void => onBroadcast(video.currentTime, video.paused);

    video.addEventListener('pause', onPause);
    video.addEventListener('play', onPlay);
    video.addEventListener('seeked', onSeeked);

    return () => {
      clearInterval(timer);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('play', onPlay);
      video.removeEventListener('seeked', onSeeked);
    };
  }, [ready, isLeader, videoRef, onBroadcast]);

  /* 2. Takt followera — korekta dryfu powyżej progu. */
  useEffect(() => {
    if (!ready || isLeader) return;

    const timer = setInterval(() => {
      const video = videoRef.current;
      if (video === null) return;
      const drift = Math.abs(video.currentTime - expectedNow());
      if (drift > DRIFT_THRESHOLD) {
        video.currentTime = expectedNow();
        setCorrectedAt(Date.now());
      }
    }, FOLLOWER_TICK_MS);

    return () => clearInterval(timer);
  }, [ready, isLeader, videoRef, expectedNow]);

  /* 3. Natychmiastowe zastosowanie zmiany stanu od prowadzącego. */
  useEffect(() => {
    if (!ready || isLeader) return;
    const video = videoRef.current;
    if (video === null) return;

    const expected = computeExpectedTime({
      syncTime: sync.time,
      syncEmittedAt: sync.emittedAt,
      isPaused: sync.paused,
      clockOffset: offsetMs,
    });

    if (Math.abs(video.currentTime - expected) > DRIFT_THRESHOLD) {
      video.currentTime = expected;
    }
    // Pauza i wznowienie stosowane bezwarunkowo: to stan, nie pozycja, i nie ma
    // sensownego progu tolerancji dla „gra" wobec „stoi".
    if (sync.paused && !video.paused) video.pause();
    if (!sync.paused && video.paused) void video.play().catch(() => undefined);
  }, [ready, isLeader, videoRef, sync, offsetMs]);

  /**
   * 4. Kontrola bezpieczeństwa po zmianie materiału.
   *
   * Osobna od taktu followera, bo ma inny próg i inny cel: takt pilnuje
   * bieżącego dryfu, a to sprawdzenie łapie klienta, który po zmianie odcinka
   * utknął na buforowaniu i nie dogonił mimo kolejnych taktów. Tolerancja jest
   * wyższa, bo pierwsze sekundy po zmianie materiału są z natury nierówne.
   *
   * Zależność od `mediaEpoch`, nie od `sync`: sprawdzenie ma nastąpić RAZ po
   * zmianie materiału, a nie restartować się przy każdym takcie prowadzącego.
   */
  useEffect(() => {
    if (!ready || isLeader) return;
    // Epoka zero znaczy, że materiał nie został jeszcze ustawiony — nie ma czego
    // sprawdzać, a stoper i tak zostałby zaraz zastąpiony przy pierwszej zmianie.
    if (mediaEpoch === 0) return;

    const timer = setTimeout(() => {
      const video = videoRef.current;
      if (video === null) return;
      if (Math.abs(video.currentTime - expectedNow()) > POST_CHANGE_TOLERANCE) {
        video.currentTime = expectedNow();
        setCorrectedAt(Date.now());
      }
    }, POST_CHANGE_CHECK_MS);

    return () => clearTimeout(timer);
  }, [ready, isLeader, mediaEpoch, videoRef, expectedNow]);

  /* Zgłoszenie końca materiału — decyzję o przejściu podejmuje serwer po kworum. */
  useEffect(() => {
    if (!ready) return;
    const video = videoRef.current;
    if (video === null) return;

    const handler = (): void => onEnded();
    video.addEventListener('ended', handler);
    return () => video.removeEventListener('ended', handler);
  }, [ready, videoRef, onEnded]);

  return { correctedAt };
}
